/**
 * P11 Service
 * Handles P11 document generation and retrieval
 */

import bcClient from '../businessCentralClient';
import { BC_CONFIG } from '../config';

/**
 * Get closed fiscal years
 * @returns {Promise<Array>} List of closed fiscal years
 */
export async function getClosedFiscalYears() {
  try {
    console.log('📅 Fetching closed fiscal years for P11');

    // Get BC token
    const tokenData = await bcClient.getToken();
    const token = tokenData.access_token;

    // Construct the closed fiscal years endpoint
    const endpoint = `/v2.0/${BC_CONFIG.tenantId}/${BC_CONFIG.environment}/api/dogma/payroll/v2.0/companies(${BC_CONFIG.companyId})/closedFiscalYears`;

    console.log('Closed Fiscal Years Endpoint:', endpoint);

    // Call the endpoint
    const response = await bcClient.get(endpoint, token);

    console.log('✅ Closed fiscal years retrieved successfully');

    // The response should contain the fiscal years in a 'value' array
    if (response && response.value) {
      return response.value;
    } else if (Array.isArray(response)) {
      return response;
    } else {
      throw new Error('Invalid response format from closed fiscal years endpoint');
    }
  } catch (error) {
    console.error('❌ Error fetching closed fiscal years:', error);
    throw error;
  }
}

/**
 * Get P11 records for employee
 * @param {string} employeeNo - Employee number
 * @param {string} fiscalYear - Fiscal year to filter by (e.g., "2025/2026")
 * @returns {Promise<Array>} List of P11 records
 */
export async function getP11Records(employeeNo, fiscalYear) {
  try {
    console.log(`📄 Fetching P11 records for employee: ${employeeNo}, fiscal year: ${fiscalYear}`);

    // Get BC token
    const tokenData = await bcClient.getToken();
    const token = tokenData.access_token;

    // Construct the P11 endpoint with fiscal year filter
    const endpoint = `/v2.0/${BC_CONFIG.tenantId}/${BC_CONFIG.environment}/api/dogma/payroll/v2.0/companies(${BC_CONFIG.companyId})/empP11DDetails?$filter=employeeNo eq '${employeeNo}' and status eq 'Released' and fiscalYear eq '${fiscalYear}'`;

    console.log('P11 Endpoint:', endpoint);

    // Call the P11 endpoint
    const response = await bcClient.get(endpoint, token, {});

    console.log('✅ P11 records retrieved successfully', response);

    // Handle different response formats
    if (response && response.value) {
      // Response has a 'value' array
      return response.value;
    } else if (Array.isArray(response)) {
      // Response is already an array
      return response;
    } else if (response && typeof response === 'object') {
      // Response is a single object/entity - wrap it in an array
      console.log('📝 Response is a single entity, wrapping in array');
      return [response];
    } else {
      throw new Error('Invalid response format from P11 endpoint');
    }
  } catch (error) {
    console.error('❌ Error fetching P11 records:', error);
    throw error;
  }
}

/**
 * Get P11D(b) Benefits In Kind records for employee
 * @param {string} employeeNo - Employee number
 * @returns {Promise<Array>} List of Benefits In Kind payroll elements
 */
export async function getP11DbRecords(employeeNo) {
  try {
    console.log(`📄 Fetching P11D(b) records for employee: ${employeeNo}`);

    const tokenData = await bcClient.getToken();
    const token = tokenData.access_token;

    const endpoint = `/v2.0/${BC_CONFIG.tenantId}/${BC_CONFIG.environment}/api/dogma/payroll/v2.0/companies(${BC_CONFIG.companyId})/employeePayrollElements?$filter=employeeCode eq '${employeeNo}' and typeId eq 'Benefits_x0020_In_x0020_Kind'`;

    console.log('P11D(b) Endpoint:', endpoint);

    const response = await bcClient.get(endpoint, token);

    console.log('✅ P11D(b) records retrieved successfully', response);

    if (response && response.value) {
      return response.value;
    } else if (Array.isArray(response)) {
      return response;
    } else if (response && typeof response === 'object') {
      return [response];
    } else {
      throw new Error('Invalid response format from P11D(b) endpoint');
    }
  } catch (error) {
    console.error('❌ Error fetching P11D(b) records:', error);
    throw error;
  }
}

/**
 * Generate P11D document in base64 format
 * @param {string} employeeNo - Employee number
 * @param {string} fiscalYear - Fiscal year (e.g., "2024/2025")
 * @returns {Promise<string>} Base64 encoded PDF document
 */
export async function generateP11Document(employeeNo, fiscalYear) {
  try {
    console.log(`📄 Generating P11D for employee: ${employeeNo}, fiscal year: ${fiscalYear}`);

    const tokenData = await bcClient.getToken();
    const token = tokenData.access_token;

    const endpoint = `/v2.0/${BC_CONFIG.tenantId}/${BC_CONFIG.environment}/api/dogma/payroll/v2.0/companies(${BC_CONFIG.companyId})/p11dGenerators('${employeeNo}')/Microsoft.NAV.GenerateP11DBase64`;

    console.log('P11 Generate Endpoint:', endpoint);

    const response = await bcClient.post(endpoint, token, { fiscalYear });

    console.log('✅ P11D generated successfully');

    if (response && response.value) {
      return response.value;
    } else if (typeof response === 'string') {
      return response;
    } else {
      throw new Error('Invalid response format from P11 generation endpoint');
    }
  } catch (error) {
    console.error('❌ Error generating P11D:', error);
    throw error;
  }
}

const p11Service = {
  getClosedFiscalYears,
  getP11Records,
  getP11DbRecords,
  generateP11Document,
};

export default p11Service;
